import { DomainEventsTable } from '@/app/domainEvents/DomainEventsTable';
import { JobStatusBadge } from '@/components/badges/JobStatusBadge';
import { HomeEvalOutcomeCells, HomeEvalOutcomeHeads } from '@/components/homeEval/HomeEvalOutcomeCells';
import { HomeEvalResultDetails } from '@/components/homeEval/HomeEvalResultDetails';
import IdWithCopy from '@/components/IdWithCopy';
import { JsonViewer } from '@/components/JsonViewer';
import { MetadataItem } from '@/components/MetadataItem';
import { TabNav } from '@/components/TabNav';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRowClick } from '@/lib/navigation';
import { domainEventsByIdsState } from '@/state/domainEvents.state';
import { homeEvalJobState, homeEvalResultsByJobIdState } from '@/state/homeEvalJobs.state';
import { useAsyncAtomValue, useLoadableAtomValue } from '@/state/loadableHooks';
import type { HomeEvalJobId, HomeEvalResultId } from '@rock/types';
import { ArrowLeft } from 'lucide-react';
import type { FC } from 'react';
import { Link, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';

export const HomeEvalJobDetailsPage: FC = () => {
    const { jobId } = useParams<{ jobId: HomeEvalJobId }>();
    return (
        <Routes>
            <Route index element={<Navigate replace to="./details" />} />
            <Route path="details" element={<HomeEvalJobDetailsContent jobId={jobId} />} />
            <Route path="results" element={<HomeEvalJobResultsContent jobId={jobId} />} />
            <Route path="results/:resultId" element={<HomeEvalJobResultsContent jobId={jobId} />} />
        </Routes>
    );
};

const HomeEvalJobDetailsContent: FC<{ jobId: HomeEvalJobId | undefined }> = ({ jobId }) => {
    const navigate = useNavigate();
    const [job, jobLoadable] = useLoadableAtomValue(homeEvalJobState(jobId));
    const triggerEventsData = useAsyncAtomValue(domainEventsByIdsState(job?.triggerEventIds), []);
    // Sort events by occurredAt in reverse chronological order (newest first)
    const triggerEvents = [...triggerEventsData].sort((a, b) => {
        const timeA = new Date(a.occurredAt).getTime();
        const timeB = new Date(b.occurredAt).getTime();
        return timeB - timeA; // Descending order (newest first)
    });

    if (jobLoadable.state === 'loading') {
        return (
            <Card>
                <CardContent className="p-6">
                    <div className="text-muted-foreground">Loading job details…</div>
                </CardContent>
            </Card>
        );
    }

    if (jobLoadable.state === 'hasError' || !job) {
        return (
            <Card>
                <CardContent className="p-6">
                    <div className="text-destructive">
                        {jobLoadable.state === 'hasError'
                            ? `Error loading job: ${
                                  jobLoadable.error instanceof Error
                                      ? jobLoadable.error.message
                                      : `${jobLoadable.error}`
                              }`
                            : 'Job not found'}
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            <TabNav
                tabs={[
                    { label: 'Details', to: `/system/jobs/home-eval/${jobId}/details` },
                    { label: 'Results', to: `/system/jobs/home-eval/${jobId}/results` },
                ]}
            />

            <Card>
                <CardHeader className="flex flex-row items-center gap-3 border-b-1 pb-3">
                    <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <CardTitle className="text-xl">Home Eval Job Details</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                            <MetadataItem label="Job ID" value={<IdWithCopy id={job.id} variant="mono" />} />
                            <MetadataItem
                                label="Home ID"
                                value={
                                    <IdWithCopy id={job.homeId} variant="mono" to={`/directory/homes/${job.homeId}`} />
                                }
                            />
                            <MetadataItem label="Status" value={<JobStatusBadge status={job.jobStatus} />} />
                            <MetadataItem label="Attempts" value={job.attempts} />
                        </div>
                        <div className="mt-4">
                            <Card>
                                <CardHeader className="border-b-1 pb-3">
                                    <CardTitle className="text-xl">Trigger Events</CardTitle>
                                    <CardDescription>
                                        Domain events that triggered this evaluation job ({triggerEvents.length}
                                        {job.triggerEventIds && job.triggerEventIds.length > 0
                                            ? ` of ${job.triggerEventIds.length}`
                                            : ''}
                                        )
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <DomainEventsTable events={triggerEvents} />
                                </CardContent>
                            </Card>
                        </div>
                        {job.errorMessage ? (
                            <div className="mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                                <div className="text-sm font-semibold text-destructive mb-2">Error Message</div>
                                <div className="text-sm text-foreground whitespace-pre-wrap">{job.errorMessage}</div>
                            </div>
                        ) : null}
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Job Object</CardTitle>
                    <CardDescription>The complete job payload stored in Firestore.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="max-h-[480px] overflow-auto rounded-md bg-muted p-4">
                        <JsonViewer data={job} />
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

const HomeEvalJobResultsContent: FC<{ jobId: HomeEvalJobId | undefined }> = ({ jobId }) => {
    const navigate = useNavigate();
    const onRowClick = useRowClick();
    const { resultId } = useParams<{ resultId?: HomeEvalResultId }>();
    const resultsPath = `/system/jobs/home-eval/${jobId}/results`;
    const [results, resultsLoadable] = useLoadableAtomValue(homeEvalResultsByJobIdState(jobId), []);
    const selectedResult = resultId ? results.find((r) => r.id === resultId) : undefined;

    if (resultsLoadable.state === 'loading') {
        return (
            <Card>
                <CardContent className="p-6">
                    <div className="text-muted-foreground">Loading results…</div>
                </CardContent>
            </Card>
        );
    }

    if (resultsLoadable.state === 'hasError') {
        return (
            <Card>
                <CardContent className="p-6">
                    <div className="text-destructive">
                        Error loading results:{' '}
                        {resultsLoadable.error instanceof Error
                            ? resultsLoadable.error.message
                            : `${resultsLoadable.error}`}
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            <TabNav
                tabs={[
                    { label: 'Details', to: `/system/jobs/home-eval/${jobId}/details` },
                    { label: 'Results', to: `/system/jobs/home-eval/${jobId}/results` },
                ]}
            />

            <Card>
                <CardHeader className="flex flex-row items-center gap-3 border-b-1 pb-3">
                    <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <CardTitle className="text-xl">Results</CardTitle>
                </CardHeader>
                <CardContent>
                    {results.length === 0 ? (
                        <div className="text-muted-foreground">
                            No results for this job. A result is written only when an evaluation creates a
                            consideration.
                        </div>
                    ) : (
                        <div className="px-4 pb-4">
                            <Table className="not-prose">
                                <TableHeader className="bg-secondary">
                                    <TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Consideration Type ID</TableHead>
                                        <HomeEvalOutcomeHeads />
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {results.map((result) => (
                                        <TableRow
                                            key={result.id}
                                            onClick={onRowClick(
                                                result.id === resultId ? resultsPath : `${resultsPath}/${result.id}`,
                                            )}
                                            className={`cursor-pointer ${result.id === resultId ? 'bg-muted' : ''}`}
                                        >
                                            <TableCell>{new Date(result.createdAt).toLocaleString()}</TableCell>
                                            <TableCell className="font-mono text-xs">
                                                <Link
                                                    to={`/content/considerations/${result.considerationTypeId}`}
                                                    className="text-sky-500 hover:underline"
                                                >
                                                    {result.considerationTypeId}
                                                </Link>
                                            </TableCell>
                                            <HomeEvalOutcomeCells result={result} />
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {selectedResult ? <HomeEvalResultDetails result={selectedResult} /> : null}
        </div>
    );
};
